/**
 * 파편 렌더러 (research §4, draw call 예산).
 * 파편을 **머티리얼 프로파일별 InstancedMesh 풀**로 렌더한다 — 각 오브젝트가 자기 재질
 * (세라믹/금속/테라코타/잎/화면발광/종이…)로 부서지도록 표면 질감(roughness·metalness·
 * emissive)을 프로파일이 결정하고, 색상은 instanceColor로 틴트한다. 파편은 유닛 큐브를
 * 청크 크기로 스케일해 파편 형상 표현. mesh.count는 high-water mark(활성 최대 인덱스+1)로
 * 유지하고 활성 0이면 visible=false — 유휴 시 컬러/섀도/GTAO 프리패스에서 완전히 빠진다.
 */
import * as THREE from 'three';

export type DebrisProfile =
  | 'ceramic'
  | 'metal'
  | 'plasticDark'
  | 'keycap'
  | 'screen'
  | 'rubber';

const PER_PROFILE_CAPACITY = 128; // 전역 상한 120 + 여유

/** 프로파일 → 머티리얼 팩토리. base color는 흰색이라 instanceColor가 그대로 틴트된다. */
function makeMaterials(): Record<DebrisProfile, THREE.MeshStandardMaterial> {
  const std = (o: THREE.MeshStandardMaterialParameters) =>
    new THREE.MeshStandardMaterial({ color: 0xffffff, ...o });
  return {
    ceramic: std({ roughness: 0.32, metalness: 0.05 }),
    metal: std({ roughness: 0.28, metalness: 0.95 }),
    plasticDark: std({ roughness: 0.5, metalness: 0.2 }),
    keycap: std({ roughness: 0.7, metalness: 0.05 }),
    // 화면 파편은 은은히 발광(부서져도 픽셀이 살아있는 느낌).
    screen: std({ roughness: 0.22, metalness: 0.1, emissive: 0x1f4bd0, emissiveIntensity: 0.5 }),
    rubber: std({ roughness: 0.7, metalness: 0.1 }),
  };
}

interface Pool {
  mesh: THREE.InstancedMesh;
  free: number[];
  /** 활성 인스턴스 최대 인덱스+1 — flush에서 mesh.count로 반영(빈 풀 정점 처리 방지). */
  highWater: number;
}

interface Slot {
  profile: DebrisProfile;
  index: number;
  size: THREE.Vector3;
}

/** 인스턴스 컬러 세팅용 재사용 THREE.Color(할당 방지). */
const WHITE = new THREE.Color(0xffffff);
const _c = new THREE.Color();

export class DebrisRenderer {
  private pools = new Map<DebrisProfile, Pool>();
  private slotOf = new Map<number, Slot>();
  private dummy = new THREE.Object3D();
  private hidden = new THREE.Object3D();

  constructor(scene: THREE.Scene) {
    this.hidden.scale.setScalar(0);
    this.hidden.updateMatrix();

    const geom = new THREE.BoxGeometry(1, 1, 1);
    const mats = makeMaterials();
    for (const key of Object.keys(mats) as DebrisProfile[]) {
      const mesh = new THREE.InstancedMesh(geom, mats[key], PER_PROFILE_CAPACITY);
      mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      mesh.count = 0; // 활성 파편이 생기면 flush가 high-water mark로 갱신
      mesh.visible = false;
      mesh.frustumCulled = false;
      // free는 내림차순으로 채워 pop()이 낮은 인덱스부터 할당 → high-water mark가 타이트하게 유지.
      const free: number[] = [];
      for (let i = PER_PROFILE_CAPACITY - 1; i >= 0; i--) {
        mesh.setMatrixAt(i, this.hidden.matrix);
        mesh.setColorAt(i, WHITE);
        free.push(i);
      }
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
      scene.add(mesh);
      this.pools.set(key, { mesh, free, highWater: 0 });
    }
  }

  hasFreeSlot(profile: DebrisProfile): boolean {
    return (this.pools.get(profile)?.free.length ?? 0) > 0;
  }

  /** 파편 인스턴스 할당. 슬롯 부족 시 false. */
  add(debrisId: number, profile: DebrisProfile, size: [number, number, number], color: number): boolean {
    const pool = this.pools.get(profile);
    if (!pool) return false;
    const index = pool.free.pop();
    if (index === undefined) return false;
    if (index + 1 > pool.highWater) pool.highWater = index + 1;
    this.slotOf.set(debrisId, { profile, index, size: new THREE.Vector3(size[0], size[1], size[2]) });
    pool.mesh.setColorAt(index, _c.set(color));
    if (pool.mesh.instanceColor) pool.mesh.instanceColor.needsUpdate = true;
    return true;
  }

  setTransform(debrisId: number, p: THREE.Vector3Like, q: THREE.QuaternionLike, fade = 1): void {
    const slot = this.slotOf.get(debrisId);
    if (!slot) return;
    const pool = this.pools.get(slot.profile);
    if (!pool) return;
    this.dummy.position.set(p.x, p.y, p.z);
    this.dummy.quaternion.set(q.x, q.y, q.z, q.w);
    this.dummy.scale.set(slot.size.x * fade, slot.size.y * fade, slot.size.z * fade);
    this.dummy.updateMatrix();
    pool.mesh.setMatrixAt(slot.index, this.dummy.matrix);
  }

  remove(debrisId: number): void {
    const slot = this.slotOf.get(debrisId);
    if (!slot) return;
    const pool = this.pools.get(slot.profile);
    if (pool) {
      pool.mesh.setMatrixAt(slot.index, this.hidden.matrix);
      pool.free.push(slot.index);
      // 전부 반환되면 high-water 리셋(부분 반환은 숨김 인스턴스 소량 잔류 — 다음 버스트에서 재사용).
      if (pool.free.length === PER_PROFILE_CAPACITY) pool.highWater = 0;
    }
    this.slotOf.delete(debrisId);
  }

  flush(): void {
    for (const pool of this.pools.values()) {
      pool.mesh.count = pool.highWater;
      pool.mesh.visible = pool.highWater > 0;
      pool.mesh.instanceMatrix.needsUpdate = true;
    }
  }

  clear(): void {
    for (const id of [...this.slotOf.keys()]) this.remove(id);
    this.flush();
  }
}
